/**
 * Building the submission.
 *
 * This is the file that decides what leaves the device, and it exists to make one guarantee
 * mechanical rather than a matter of discipline:
 *
 *   **A field the technician never saw is never submitted.**
 *
 * Concretely, three things are enforced here:
 *
 *  1. Only fields in the computed visible set are considered. A hidden field's value stays in
 *     local state, so flipping a branch back does not destroy typing, but it cannot reach the
 *     payload. Every exclusion is itemised in `omitted` with a reason, so the omission is
 *     auditable rather than invisible.
 *  2. A visible-but-unanswered optional field is omitted too, not sent as `null`. `null` is
 *     indistinguishable from "the technician deliberately entered nothing", and downstream a
 *     null overwrite is a destructive write. Absence is the honest encoding.
 *  3. Every answer carries provenance. An overridden prefill ships both values, so a reviewer
 *     can always tell whether a human asserted something or Salesforce did.
 *
 * Because of (1) and (2), `answers.length` is exactly the number of things somebody or
 * something actually asserted.
 */

import { computeVisibility, isAnswered } from './logic.ts';
import type { Visibility } from './logic.ts';
import type {
  Answer,
  FormSchema,
  FormState,
  MappingConfidence,
  PhotoRef,
  Provenance,
  SubmissionDestination,
} from './types.ts';

export type OmitReason = 'hidden' | 'unanswered-optional' | 'display-only';

export interface OmittedField {
  readonly fieldId: string;
  readonly reason: OmitReason;
}

export interface AnswerRecord {
  readonly fieldId: string;
  readonly sourceTag?: string;
  readonly kind: string;
  readonly label: string;
  readonly value: Answer;
  readonly provenance: Provenance;
  /** Present whenever the value originated from Salesforce, override or not. */
  readonly prefilledFrom?: {
    readonly source: string;
    readonly label: string;
    readonly object?: string;
    readonly field?: string;
    readonly fetchedAt: number;
  };
  /** Present only on an override: what Salesforce said before the technician corrected it. */
  readonly overrodeValue?: Answer;
  readonly overriddenAt?: number;
  readonly confirmedAt?: number;
  readonly salesforce?: {
    readonly object: string;
    readonly field: string;
    readonly confidence: MappingConfidence;
  };
}

export interface SubmissionCounts {
  /** Questions put to the technician. */
  readonly asked: number;
  /** Answers that came from Salesforce and were left alone. */
  readonly prefilled: number;
  /** Prefilled answers the technician corrected. */
  readonly overridden: number;
  /** Prefilled answers the technician explicitly affirmed. */
  readonly confirmed: number;
  /** Answers supplied by the device or the token. */
  readonly auto: number;
  /** Fields the display logic removed. */
  readonly hiddenOmitted: number;
  readonly photos: number;
}

export interface SubmissionPayload {
  readonly schemaId: string;
  readonly schemaVersion: number;
  readonly destination: SubmissionDestination;
  readonly startedAt: number;
  readonly completedAt: number;
  /** Everything the schema declared as context, carried so a reviewer can replay the logic. */
  readonly context: Readonly<Record<string, string | number | boolean | null>>;
  readonly answers: readonly AnswerRecord[];
  readonly omitted: readonly OmittedField[];
  readonly photoBlobIds: readonly string[];
  readonly counts: SubmissionCounts;
  /**
   * Mappings the schema declares but that were never verified against org metadata. Non-empty
   * means the backend must not blind-write these; it is surfaced rather than silently dropped.
   */
  readonly unverifiedMappings: readonly { fieldId: string; object: string; field: string }[];
}

function collectPhotoIds(value: Answer): string[] {
  if (Array.isArray(value)) {
    const out: string[] = [];
    for (const item of value) {
      if (typeof item === 'object' && item !== null && 'blobId' in item) out.push((item as PhotoRef).blobId);
    }
    return out;
  }
  return [];
}

export interface BuildOptions {
  readonly completedAt: number;
  readonly visibility?: Visibility;
}

export function buildSubmission(schema: FormSchema, state: FormState, options: BuildOptions): SubmissionPayload {
  const visibility = options.visibility ?? computeVisibility(schema, state);

  const answers: AnswerRecord[] = [];
  const omitted: OmittedField[] = [];
  const photoBlobIds: string[] = [];
  const unverifiedMappings: { fieldId: string; object: string; field: string }[] = [];

  let asked = 0;
  let prefilled = 0;
  let overridden = 0;
  let confirmed = 0;
  let auto = 0;
  let hiddenOmitted = 0;

  for (const section of schema.sections) {
    const sectionVisible = visibility.sections.has(section.id);
    for (const field of section.fields) {
      if (field.kind === 'note') {
        omitted.push({ fieldId: field.id, reason: 'display-only' });
        continue;
      }

      if (!sectionVisible || !visibility.fields.has(field.id)) {
        // The load-bearing line. Whatever is sitting in state for this field is discarded,
        // even if it is a required field, even if it was prefilled, even if it was answered
        // before the branch closed.
        omitted.push({ fieldId: field.id, reason: 'hidden' });
        hiddenOmitted += 1;
        continue;
      }

      const entry = state.entries[field.id];
      if (!entry || !isAnswered(entry.value)) {
        omitted.push({ fieldId: field.id, reason: 'unanswered-optional' });
        continue;
      }

      switch (entry.provenance) {
        case 'technician':
          asked += 1;
          break;
        case 'prefill':
          prefilled += 1;
          break;
        case 'override':
          overridden += 1;
          break;
        case 'auto':
          auto += 1;
          break;
      }
      if (entry.confirmedAt !== undefined) confirmed += 1;

      photoBlobIds.push(...collectPhotoIds(entry.value));

      if (field.maps?.confidence === 'unverified') {
        unverifiedMappings.push({ fieldId: field.id, object: field.maps.object, field: field.maps.field });
      }

      answers.push({
        fieldId: field.id,
        ...(field.sourceTag !== undefined ? { sourceTag: field.sourceTag } : {}),
        kind: field.kind,
        label: field.label,
        value: entry.value,
        provenance: entry.provenance,
        ...(entry.prefill
          ? {
              prefilledFrom: {
                source: entry.prefill.source,
                label: entry.prefill.label,
                ...(entry.prefill.object !== undefined ? { object: entry.prefill.object } : {}),
                ...(entry.prefill.field !== undefined ? { field: entry.prefill.field } : {}),
                fetchedAt: entry.prefill.fetchedAt,
              },
            }
          : {}),
        ...(entry.provenance === 'override' && entry.prefill
          ? { overrodeValue: entry.prefill.originalValue }
          : {}),
        ...(entry.overriddenAt !== undefined ? { overriddenAt: entry.overriddenAt } : {}),
        ...(entry.confirmedAt !== undefined ? { confirmedAt: entry.confirmedAt } : {}),
        ...(field.maps
          ? { salesforce: { object: field.maps.object, field: field.maps.field, confidence: field.maps.confidence } }
          : {}),
      });
    }
  }

  return {
    schemaId: schema.id,
    schemaVersion: schema.version,
    destination: schema.destination,
    startedAt: state.startedAt,
    completedAt: options.completedAt,
    context: state.context,
    answers,
    omitted,
    photoBlobIds,
    counts: {
      asked,
      prefilled,
      overridden,
      confirmed,
      auto,
      hiddenOmitted,
      photos: photoBlobIds.length,
    },
    unverifiedMappings,
  };
}

/**
 * Static accounting for a schema: how many questions it would ask versus supply, ignoring
 * display logic.
 *
 * This is the honest denominator for the asked-versus-prefilled ratio. Counting a hidden
 * question as "saved by prefill" would double-count the two separate levers — prefill answers
 * a question from the record, display logic decides it does not apply — so the two are
 * reported separately here and never summed.
 */
export interface SchemaAccounting {
  readonly schemaId: string;
  readonly total: number;
  readonly asked: number;
  readonly prefilled: number;
  /**
   * Fields bound to an untrusted source: the record's value is shown for comparison but the
   * technician still has to answer. Counted under `asked`, never under `prefilled`.
   */
  readonly reference: number;
  readonly notes: number;
  readonly photoFields: number;
  readonly conditional: number;
  readonly mapped: number;
  readonly mappedVerified: number;
}

/**
 * The scoreboard, built so it cannot be flattered.
 *
 * Two rules keep the prefilled count honest:
 *
 *  - A field bound to an **untrusted** source counts as **asked**, not prefilled. Otherwise
 *    binding all twenty module-serial fields to the asset records would add twenty to the
 *    prefilled column while the technician still reads twenty labels off the hardware. That is a
 *    real temptation — those bindings are useful and they are in these schemas — so the accounting
 *    has to resist it rather than the author having to remember to.
 *  - Display-only notes are excluded from `total` entirely, so padding a schema with explanatory
 *    text cannot move the ratio in either direction.
 */
export function accountFor(schema: FormSchema, isTrusted: (source: string) => boolean = () => true): SchemaAccounting {
  let total = 0;
  let asked = 0;
  let prefilledCount = 0;
  let referenceCount = 0;
  let notes = 0;
  let photoFields = 0;
  let conditional = 0;
  let mapped = 0;
  let mappedVerified = 0;

  for (const section of schema.sections) {
    for (const field of section.fields) {
      if (field.kind === 'note') {
        notes += 1;
        continue;
      }
      total += 1;
      if (field.prefill && isTrusted(field.prefill.source)) {
        prefilledCount += 1;
      } else {
        asked += 1;
        if (field.prefill) referenceCount += 1;
      }
      if (field.kind === 'photo') photoFields += 1;
      if (field.showIf !== undefined || section.showIf !== undefined) conditional += 1;
      if (field.maps) {
        mapped += 1;
        if (field.maps.confidence === 'verified') mappedVerified += 1;
      }
    }
  }

  return {
    schemaId: schema.id,
    total,
    asked,
    prefilled: prefilledCount,
    reference: referenceCount,
    notes,
    photoFields,
    conditional,
    mapped,
    mappedVerified,
  };
}
